import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth } from '../hooks/useAuth';

interface LocationState {
  from?: { pathname?: string };
}

type Mode = 'sign-in' | 'sign-up';

/**
 * Sign-in page. Customers use email + password (also Google for anyone with
 * a WCA Google account). Sessions persist across reloads, tab close, and
 * browser restarts via firebase.ts's browserLocalPersistence — sign in
 * once, stay signed in until you explicitly sign out.
 */
export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthed, role } = useAuth();
  const [mode, setMode] = useState<Mode>('sign-in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Resolve where to send the user after sign-in based on their role.
  // Baristas always go to /kitchen — the rest of the app is invisible to
  // them. Everyone else returns to wherever they were heading (or /order).
  const resolveDestination = useCallback(
    (currentRole: typeof role): string => {
      if (currentRole === 'barista') return '/kitchen';
      return (location.state as LocationState | null)?.from?.pathname ?? '/order';
    },
    [location.state],
  );

  // Safety net: Safari (and occasionally Chrome on certain network
  // conditions) sometimes resolves signInWithPopup's promise after a
  // delay or not at all, leaving the user "logged in" but visually
  // stuck on /login. React to the auth+role state directly so we land
  // on the right page regardless of which auth method actually finished.
  // We wait until `role` is non-null so we don't briefly send a barista
  // to /order and bounce them back through RoleGuard.
  useEffect(() => {
    if (isAuthed && role !== null) {
      navigate(resolveDestination(role), { replace: true });
    }
  }, [isAuthed, role, resolveDestination, navigate]);

  const redirectAfterAuth = (): void => {
    navigate(resolveDestination(role), { replace: true });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (mode === 'sign-in') {
        await signInWithEmailAndPassword(auth, email.trim(), password);
      } else {
        await createUserWithEmailAndPassword(auth, email.trim(), password);
      }
      redirectAfterAuth();
    } catch (err) {
      setError(prettyAuthError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = async (): Promise<void> => {
    setError(null);
    setSubmitting(true);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(auth, provider);
      redirectAfterAuth();
    } catch (err) {
      setError(prettyAuthError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const isSignUp = mode === 'sign-up';

  const inputClass =
    'w-full rounded-xl border border-wca-gray-light bg-wca-chalk/40 px-3.5 py-3 text-sm transition-colors focus:border-wca-red focus:bg-wca-surface focus:outline-none focus:ring-2 focus:ring-wca-red/25';

  return (
    <section className="relative flex min-h-dvh flex-col items-center justify-center overflow-hidden px-6 py-12">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_42%_at_50%_0%,rgba(196,18,48,0.07),transparent_60%)]" />

      <Link to="/" className="relative mb-7 font-display text-2xl text-wca-black">
        The Cracked Mug<span className="text-wca-gold">.</span>
      </Link>

      <div className="relative w-full max-w-md rounded-2xl border border-wca-black/[0.06] bg-wca-surface p-7 shadow-card-lg sm:p-8">
        <h1 className="font-display text-3xl text-wca-black">
          {isSignUp ? 'Create your account' : 'Welcome back'}
        </h1>
        <p className="mb-6 mt-1 text-sm text-wca-gray-mid">
          {isSignUp
            ? 'A Cracked Mug account lets you order from your classroom.'
            : 'Sign in to order from The Cracked Mug.'}
        </p>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div>
            <label htmlFor="email" className="mb-1.5 block text-sm font-medium">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete={isSignUp ? 'email' : 'username'}
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="password" className="mb-1.5 block text-sm font-medium">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete={isSignUp ? 'new-password' : 'current-password'}
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClass}
            />
            {isSignUp && <p className="mt-1 text-xs text-wca-gray-mid">At least 6 characters.</p>}
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="inline-flex min-h-[44px] w-full items-center justify-center rounded-full bg-wca-red py-3.5 font-semibold text-white shadow-[0_8px_20px_-6px_rgba(196,18,48,0.5)] transition-all hover:bg-wca-red-dark active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60 disabled:active:scale-100"
          >
            {submitting ? 'Please wait…' : isSignUp ? 'Create account' : 'Sign in'}
          </button>
        </form>

        <div className="my-5 flex items-center gap-3 text-xs text-wca-gray-mid">
          <span className="h-px flex-1 bg-wca-gray-light" />
          or
          <span className="h-px flex-1 bg-wca-gray-light" />
        </div>

        <button
          type="button"
          onClick={() => void handleGoogle()}
          disabled={submitting}
          className="inline-flex min-h-[44px] w-full items-center justify-center rounded-full border border-wca-gray-light bg-wca-surface py-3.5 font-semibold text-wca-black transition-colors hover:bg-wca-gray-light/50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Continue with Google
        </button>

        <p className="mt-6 text-center text-sm text-wca-gray-mid">
          {isSignUp ? 'Already have an account?' : 'New here?'}{' '}
          <button
            type="button"
            onClick={() => {
              setMode(isSignUp ? 'sign-in' : 'sign-up');
              setError(null);
            }}
            className="font-semibold text-wca-red hover:text-wca-red-dark"
          >
            {isSignUp ? 'Sign in' : 'Create an account'}
          </button>
        </p>

        {error !== null && (
          <p className="mt-4 rounded-xl bg-wca-red-light/60 p-3 text-sm text-wca-red" role="alert">
            {error}
          </p>
        )}
      </div>

      <Link
        to="/privacy"
        className="relative mt-6 text-xs text-wca-gray-mid underline hover:text-wca-black"
      >
        Privacy policy
      </Link>
    </section>
  );
}

function prettyAuthError(err: unknown): string {
  if (!(err instanceof Error)) return 'Sign-in failed';
  const code = 'code' in err ? String((err as { code: unknown }).code) : '';
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Email or password is incorrect.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists — try signing in.';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/operation-not-allowed':
      return 'Email sign-in is not enabled yet. Enable Email/Password in the Firebase Authentication console.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'Sign-in canceled.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    default:
      return err.message;
  }
}
